import { Modal, Card } from "react-bootstrap";
import { Link } from "react-router-dom";
import { ChangeNameReqApi, IntegrationStatus, SubscriptionStatus } from "./Data/ApiTransport";
import {
  SubscriptionParker,
  Vehicle,
  ParkerUpdateReq,
} from "./Data/ApiTypes";
import { useEffect, useMemo, useState } from "react";
import { Api } from "./Data/Api";
import { processResult } from "./Data/Result";
import { InfoCircleFill, Person, At, Telephone, CheckCircleFill } from "react-bootstrap-icons";
import { Button } from "./Controls/Button";
import { Input } from "./Controls/Input";
import { PhoneInput } from "./Controls/PhoneInput";
import { useForm } from "react-hook-form";
import { emitSubscriptionParker } from "./Data/ApiEmit";
import { toast } from "react-toastify";
import { AddVehicleModal } from "./Controls/AddVehicleModal";
import { ChooseVehicleModal } from "./Controls/ChooseVehicleModal";
import QrCodeModal from './Controls/QRCodeModal';
import { createApiService } from "./shared/apiConfig";
import { DataEntitiesFacilityIntegrationParty, FacilityApi, ParkerIntegrationPartyApi, ServerRequestsParkerIntegrationPartyDto } from "./shared/api/axios-client";
import { ConfirmModal } from "./Controls/ConfirmModal";
import { ParkerIntegrations } from "./ParkerIntegrations";
import { canPerformAction, extractClaims } from "./helpers/AuthorizationManager";
import { authAtom } from "./Data/Atoms";
import { useAtom } from "jotai";
import { SUBSCRIPTION_ALLOWED_THIRD_PARTY, VEHICLE_ALLOWED_THIRD_PARTY, isSubscriptionLevelIntegration, isVehicleLevelIntegration } from "./FacilityParkerSubscriptionEditPage";
import { VehiclesSection } from "./VehiclesSection";
import { useSubscriptionVehiclesAndIntegrations } from "./useSubscriptionVehiclesAndIntegrations";
import { getInitials, avatarColor } from "./Data/Avatar";

export interface SearchSubParkerContext {
  mainAccountName: string;
  mainParkerId: number;
  subscriptionId: number;
}

interface Props {
  show: boolean;
  onClose: () => void;
  parker: SubscriptionParker;
  setParker: React.Dispatch<
    React.SetStateAction<SubscriptionParker | undefined>
  >;
  parkers: any[];
  setParkers: React.Dispatch<React.SetStateAction<any[]>>;
  selectedParkerSub: any;
  facilityId: number;
  searchSubParkerContext?: SearchSubParkerContext;
}

const ParkerEditModal = ({
  show,
  onClose,
  parker,
  setParker,
  parkers,
  setParkers,
  selectedParkerSub,
  facilityId,
  searchSubParkerContext,
}: Props) => {
  const hasMoreVehiclesThanSpots = parker.vehicles.length > parker.quantity;
  const allowedSpots = parker.quantity;

  const initialValues = useMemo(
    () => ({
      ...emitSubscriptionParker(parker),
      parker: {
        ...emitSubscriptionParker(parker).parker,
        emailAddress: parker.parker.isAutoGeneratedEmail
          ? ""
          : parker.parker.emailAddress,
        phoneNumber: parker.parker.phoneNumber ?? "",
      },
    }),
    [parker]
  );

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { isDirty, errors },
  } = useForm<SubscriptionParker>({
    defaultValues: initialValues,
  });

  const values = watch();
  const [showAdd, setShowAdd] = useState(false);
  const [showChoose, setShowChoose] = useState(false);
  const [chooseModalVehicles, setChooseModalVehicles] = useState<Vehicle[]>([]);
  const [chooseModalExclude, setChooseModalExclude] = useState<number[]>([]);
  const [integrationQrCodeData, setIntegrationQrCodeData] = useState<string>('');
  const [showIntegrationQrModal, setShowIntegrationQrModal] = useState(false);
  const [showToggleConfirm, setShowToggleConfirm] = useState(false);
  const [showParkerVehicleRemoveConfirm, setShowParkerVehicleRemoveConfirm] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState<Vehicle | null>(null);
  const [pendingToggle, setPendingToggle] = useState<{
    integrationPartyId: number;
    subscriptionId: number;
    parkerId: number;
    facilityId: number;
    newStatus: IntegrationStatus;
    vehicleId?: number;
  } | null>(null);
  const [parkerIntegrations, setParkerIntegrations] = useState<ServerRequestsParkerIntegrationPartyDto[]>([]);
  const [facilityIntegrations, setFacilityIntegrations] = useState<
    DataEntitiesFacilityIntegrationParty[]
  >([]);
  const [auth, setAuth] = useAtom(authAtom);
  const canManageMonthly = canPerformAction(extractClaims(auth), 'manage-monthly');
  const {
      vehicleAdd,
      vehicleLink,
      vehicleRemove,
      openChooseModalForParkersList,
      handleToggleIntegration,
    } = useSubscriptionVehiclesAndIntegrations({
      facilityId,
      parkerId: parker.parker.parkerId,
      subscriptionId: selectedParkerSub.subscriptionId,
      vehicles: parker.vehicles,
      setParker,
      setParkers,
      setParkerIntegrations,
      setChooseModalVehicles,
      setChooseModalExclude,
      setShowChoose,
  });

  useEffect(() => {
    reset({
      ...emitSubscriptionParker(parker),
      parker: {
        ...emitSubscriptionParker(parker).parker,
        emailAddress: parker.parker.isAutoGeneratedEmail
          ? ""
          : parker.parker.emailAddress,
        phoneNumber: parker.parker.phoneNumber ?? "",
      },
    });
  }, [parker, reset]);

  const canManageIntegrations = canPerformAction(
    extractClaims(auth),
    "manage-integrations"
  );

  const subscriptionIntegrations = useMemo(() => {
    const subscriptionsIntegrations = facilityIntegrations.filter(
      (x) =>
        SUBSCRIPTION_ALLOWED_THIRD_PARTY.includes(x.integrationParty?.name || "") &&
        !parkerIntegrations.some(
          (i) =>
            i.integrationPartyId === x.integrationPartyId &&
            isSubscriptionLevelIntegration(i)
        ) &&
        x.isActive
    );

    return [
      ...parkerIntegrations.filter(
        (fi) =>
          SUBSCRIPTION_ALLOWED_THIRD_PARTY.includes(fi.integrationName || "") &&
          isSubscriptionLevelIntegration(fi)
      ),
      ...subscriptionsIntegrations.map((fi) => ({
        integrationPartyId: fi.integrationPartyId,
        integrationName: fi.integrationParty?.name,
        parkerId: parker.parker.parkerId,
        isActive: false,
        metaFlashUID: "",
        metaFlashRFIDNumber: "",
      })),
    ].filter((x): x is NonNullable<typeof x> => x != null);
  }, [facilityIntegrations, parkerIntegrations, parker.parker.parkerId]);

  const vehicleIntegrations = useMemo(() => {
    const vehiclesIntegrations = facilityIntegrations.filter(
      (x) =>
        VEHICLE_ALLOWED_THIRD_PARTY.includes(x.integrationParty?.name || "") &&
        x.isActive
    );

    return [
      ...parkerIntegrations.filter(
        (fi) =>
          VEHICLE_ALLOWED_THIRD_PARTY.includes(fi.integrationName || "") &&
          isVehicleLevelIntegration(fi)
      ),
      ...parker.vehicles.flatMap((vehicle) =>
        vehiclesIntegrations
          .filter(
            (fi) =>
              !parkerIntegrations.some(
                (pi) =>
                  pi.integrationPartyId === fi.integrationPartyId &&
                  pi.vehicleId === vehicle.vehicleId
              )
          )
          .map((fi) => ({
            integrationPartyId: fi.integrationPartyId,
            integrationName: fi.integrationParty?.name || "Ballparc",
            parkerId: parker.parker.parkerId,
            vehicleId: vehicle.vehicleId,
            isActive: false,
            metaFlashUID: "",
            metaFlashRFIDNumber: "",
          }))
      ),
    ].filter((x): x is NonNullable<typeof x> => x != null);
  }, [facilityIntegrations, parkerIntegrations, parker.parker.parkerId, parker.vehicles]);

  useEffect(() => {
    if (!show || !canManageIntegrations || !facilityId) {
      if (!show) {
        setFacilityIntegrations([]);
        setParkerIntegrations([]);
      }
      return;
    }

    let cancelled = false;

    async function fetchIntegrations() {
      try {
        const facilityApi = createApiService(FacilityApi);
        const integrationApi = createApiService(ParkerIntegrationPartyApi);

        const [facilityResponse, integrationResponse] = await Promise.all([
          facilityApi.getFacilityIntegrations({ facilityId }),
          integrationApi.listParkerIntegrationParties({
            subscriptionId: selectedParkerSub.subscriptionId,
          }),
        ]);

        if (cancelled) return;

        const enrolledIntegrations =
          integrationResponse.data.items?.filter(
            (x) => x.parkerId === parker.parker.parkerId
          ) ?? [];

        setParkerIntegrations(enrolledIntegrations);
        setFacilityIntegrations(facilityResponse.data);
      } catch (err: any) {
        console.error("Failed to fetch integrations:", err);
        toast.error("Failed to load access code or integrations.");
      }
    }

    fetchIntegrations();

    return () => {
      cancelled = true;
    };
  }, [
    show,
    facilityId,
    canManageIntegrations,
    parker.parker.parkerId,
    selectedParkerSub.subscriptionId,
  ]);

  const onSubmit = async (data: SubscriptionParker) => {
    let nameUpdated = false;
    let contactUpdated = false;

    const nameChanged =
      !parker.parker.isManaged && data.parker.name !== parker.parker.name;
    const displayedEmail = parker.parker.isAutoGeneratedEmail
      ? ""
      : parker.parker.emailAddress;
    const newEmail = (data.parker.emailAddress ?? "").trim();
    const emailChanged = newEmail !== displayedEmail;
    const newPhone = (data.parker.phoneNumber ?? "").trim();
    const oldPhone = (parker.parker.phoneNumber ?? "").trim();
    const phoneChanged = newPhone !== oldPhone;

    if (nameChanged) {
      const nameReq: ChangeNameReqApi = {
        name: data.parker.name,
      };

      await Api.changeParkerName(parker.parker.parkerId, nameReq).then(
        (resp: any) =>
          processResult(
            resp,
            () => {
              nameUpdated = true;
            },
            (err) => toast.error(err as string)
          )
      );
    }

    // ParkerUpdateReq requires a formatted phone (shared with main Contact modal).
    // Empty clear is not supported here — keep existing when blank.
    if ((emailChanged && newEmail !== "") || (phoneChanged && !!newPhone)) {
      const updateReq: ParkerUpdateReq = {
        name: nameChanged ? data.parker.name : parker.parker.name,
        emailAddress: newEmail !== "" ? newEmail : parker.parker.emailAddress,
        phoneNumber: newPhone || oldPhone,
        note: "",
      };

      await Api.parkerUpdate(parker.parker.parkerId, updateReq).then((resp: any) =>
        processResult(
          resp,
          () => {
            contactUpdated = true;
          },
          (err) => toast.error(err as string)
        )
      );
    }

    if (nameUpdated || contactUpdated) {
      const updatedParker: SubscriptionParker = {
        ...parker,
        quantity: data.quantity,
        parker: {
          ...parker.parker,
          name: nameUpdated ? data.parker.name : parker.parker.name,
          emailAddress: contactUpdated && newEmail !== ""
            ? newEmail
            : parker.parker.emailAddress,
          isAutoGeneratedEmail: contactUpdated && newEmail !== ""
            ? false
            : parker.parker.isAutoGeneratedEmail,
          phoneNumber: contactUpdated && newPhone
            ? newPhone
            : parker.parker.phoneNumber,
        },
      };

      setParker(updatedParker);

      const updatedParkers = parkers.map((p) =>
        p.parker.parkerId === updatedParker.parker.parkerId ? updatedParker : p
      );

      setParkers(updatedParkers);
      reset({
        ...updatedParker,
        parker: {
          ...updatedParker.parker,
          emailAddress: updatedParker.parker.isAutoGeneratedEmail
            ? ""
            : updatedParker.parker.emailAddress,
          phoneNumber: updatedParker.parker.phoneNumber ?? "",
        },
      });
      toast.success("Parker Updated Successfully");
    }
  };

  const displayName =
    watch("parker.name")?.trim() || parker.parker.name || "Parker";

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton className="align-items-center border-0 pb-0">
        <div className="d-flex align-items-center gap-3 min-w-0">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 fw-bold text-white"
            style={{
              width: 40,
              height: 40,
              fontSize: "0.85rem",
              backgroundColor: avatarColor(displayName),
            }}
          >
            {getInitials(displayName).slice(0, 1)}
          </div>
          <div className="min-w-0">
            <div
              className="text-uppercase text-muted fw-semibold"
              style={{ fontSize: "0.7rem", letterSpacing: "0.04em", lineHeight: 1.2 }}
            >
              Manage Parker
            </div>
            <Modal.Title className="mb-0 text-truncate" as="h5">
              {displayName}
            </Modal.Title>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        {searchSubParkerContext && (
          <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-3">
            <InfoCircleFill className="mt-1 flex-shrink-0" />
            <div>
              <div className="small mb-2">
                This account is part of{" "}
                <strong>{searchSubParkerContext.mainAccountName}</strong>.
              </div>
              <Link
                className="small"
                to={`/facility/${facilityId}/parker/${searchSubParkerContext.mainParkerId}/plan/${searchSubParkerContext.subscriptionId}`}
                onClick={onClose}
              >
                View Subscription
              </Link>
            </div>
          </div>
        )}
        {hasMoreVehiclesThanSpots && (
          <div
            className="alert alert-purple my-2 d-flex align-items-center gap-2"
            style={{ backgroundColor: "var(--color-light)" }}
          >
            <InfoCircleFill />
            <div>
              There are more vehicles than spaces available. Only{" "}
              <strong>{allowedSpots}</strong> vehicle
              {allowedSpots > 1 ? "s" : ""} are permitted to be parked at a
              time.
            </div>
          </div>
        )}
        <Card className="mb-4">
          <Card.Header className="d-flex align-items-center gap-2 py-2">
            <Person size={16} className="text-muted flex-shrink-0" />
            <span>Contact Info</span>
          </Card.Header>
          <div className="px-3 py-3">
            {!parker.parker.isManaged && (
              <div className="mb-3">
                <Input
                  icon={<Person />}
                  type="text"
                  label="Name"
                  disabled={selectedParkerSub.status != SubscriptionStatus.Active || !canManageMonthly}
                  inputClassName="text-start"
                  className="mb-0"
                  {...register("parker.name", {
                    required: "Name is required",
                  })}
                  error={errors.parker?.name}
                />
              </div>
            )}
            <div className="row g-3 mb-3">
              <div className="col-12 col-md-6">
                <Input
                  icon={<At />}
                  type="email"
                  label="Email address"
                  disabled={selectedParkerSub.status != SubscriptionStatus.Active || !canManageMonthly}
                  inputClassName="text-start"
                  className="mb-0"
                  {...register("parker.emailAddress", {
                    validate: (value) => {
                      const trimmed = value?.trim() ?? "";
                      if (
                        trimmed &&
                        !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(trimmed)
                      ) {
                        return "Invalid email address";
                      }
                      return true;
                    },
                  })}
                  error={errors.parker?.emailAddress}
                />
              </div>
              <div className="col-12 col-md-6">
                <PhoneInput
                  icon={<Telephone />}
                  label="Phone number"
                  placeholder="555-555-5555"
                  disabled={selectedParkerSub.status != SubscriptionStatus.Active || !canManageMonthly}
                  className="mb-0"
                  value={watch("parker.phoneNumber") ?? ""}
                  {...register("parker.phoneNumber", {
                    pattern: {
                      value: /^$|^\d{3}-\d{3}-\d{4}$/,
                      message: "Phone number must be in format ###-###-####",
                    },
                  })}
                  error={errors.parker?.phoneNumber}
                />
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-between gap-2">
              {!isDirty ? (
                <span className="text-muted small d-inline-flex align-items-center gap-1">
                  <CheckCircleFill className="text-success flex-shrink-0" size={14} />
                  All changes saved
                </span>
              ) : (
                <span />
              )}
              <Button
                type="button"
                className={`rounded-pill px-3 py-1 ${
                  isDirty && canManageMonthly
                    ? "btn-main"
                    : "bg-secondary-subtle text-muted border-0"
                }`}
                style={{ width: "auto", minWidth: "4.5rem" }}
                onClick={handleSubmit(onSubmit)}
                disabled={!isDirty || !canManageMonthly}
              >
                Save
              </Button>
            </div>
          </div>
        </Card>

          {(selectedParkerSub.facilityHasIntegrationsEnabled || selectedParkerSub?.facility?.hasIntegrationsEnabled) && canManageIntegrations && (
            <ParkerIntegrations
              integrations={subscriptionIntegrations}
              subscriptionId={selectedParkerSub.subscriptionId}
              facilityId={facilityId}
              setPendingToggle={setPendingToggle}
              setShowToggleConfirm={setShowToggleConfirm}
              setQrCodeData={setIntegrationQrCodeData}
              setShowQrModal={setShowIntegrationQrModal}
              setParkerIntegrations={setParkerIntegrations}
            />
          )}

        <VehiclesSection
          vehicles={parker.vehicles}
          subscriptionStatus={selectedParkerSub.status}
          facilityId={facilityId}
          subscriptionId={selectedParkerSub.subscriptionId}
          parkerId={parker.parker.parkerId}
          integrations={vehicleIntegrations}
          onAddNew={() => setShowAdd(true)}
          onAddExisting={openChooseModalForParkersList}
          onRemove={(v) => {
            setVehicleToDelete(v);
            setShowParkerVehicleRemoveConfirm(true);
          }}
          onResyncVehicleIntegration={async (
            integrationPartyId,
            integrationParkerId,
            vehicleId
          ) => {
            await handleToggleIntegration(
              integrationPartyId,
              selectedParkerSub.subscriptionId,
              integrationParkerId,
              vehicleId,
              IntegrationStatus.Activated
            );
          }}
          setPendingToggle={setPendingToggle}
          setShowToggleConfirm={setShowToggleConfirm}
        />

      </Modal.Body>
      <Modal.Footer>
        <Button
          type="button"
          className="w-100"
          onClick={() => {
            onClose();
            reset();
          }}
        >
          Close
        </Button>
      </Modal.Footer>

      <AddVehicleModal
        show={showAdd}
        setShow={setShowAdd}
        onSubmit={vehicleAdd}
        showMask={false}
      />

      <ChooseVehicleModal
        show={showChoose}
        setShow={setShowChoose}
        onSubmit={vehicleLink}
        vehicles={chooseModalVehicles}
        exclude={chooseModalExclude}
        onClose={() => setShowChoose(false)}
        showMask={false}
      />

      <QrCodeModal
        show={showIntegrationQrModal}
        onHide={() => setShowIntegrationQrModal(false)}
        value={integrationQrCodeData}
        height={256}
        showMask={false}
      />
      
      <ConfirmModal
        title={`${pendingToggle?.newStatus === IntegrationStatus.Activated ? "Activate" : "Deactivate"}`}
        message={`Are you sure you want to ${pendingToggle?.newStatus === IntegrationStatus.Activated ? "activate" : "deactivate"}?`}
        show={showToggleConfirm}
        setShow={setShowToggleConfirm}
        backdrop={false}
        onConfirm={() => {
          if (pendingToggle) {
            handleToggleIntegration(
              pendingToggle.integrationPartyId,
              pendingToggle.subscriptionId,
              pendingToggle.parkerId,
              pendingToggle.vehicleId,
              pendingToggle.newStatus,
            );
          }
          setShowToggleConfirm(false);
          setPendingToggle(null);
        }}
      />

      <ConfirmModal
        title={`Remove Vehicle`}
        message={`Are you sure you want to remove this vehicle from the parker?`}
        show={showParkerVehicleRemoveConfirm}
        setShow={setShowParkerVehicleRemoveConfirm}
        backdrop={false}
        onConfirm={() => {
          if (vehicleToDelete)
          {
            vehicleRemove(vehicleToDelete)
          }
          setVehicleToDelete(null)
          setShowParkerVehicleRemoveConfirm(false);
        }}
      />
    </Modal>
  );
};

export default ParkerEditModal;
